// ReviewTable.jsx

import React, { Component } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Divider,
  TablePagination,
  CircularProgress,
  Card,
  CardContent,
  withStyles,
  Button
} from '@material-ui/core';
import { ArrowRightAlt } from '@material-ui/icons';
import { FormattedMessage } from 'react-intl';

const styles = (theme) => ({
  container: {
    width: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    color: '#0087B6',
    borderBottom: '2px solid #0087B6',
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(3),
    fontSize: '1.5rem',
    fontWeight: 'bold'
  },
  reviewItem: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: '8px',
    backgroundColor: '#f9fafe',
    border: '1px solid #e0e0e0',
    cursor: 'pointer',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[6],
    }
  },
  textContent: {
    marginBottom: theme.spacing(2),
  },
  bookInfo: {
    marginTop: theme.spacing(1),
    display: 'block',
    color: theme.palette.text.secondary,
  },
  scoreContainer: {
    textAlign: 'center',
    padding: theme.spacing(1),
    borderRadius: '4px',
    minWidth: '120px',
  },
  scoreNumber: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
  },
  scoreLabel: {
    fontSize: '0.875rem',
  },
  wordsInfo: {
    fontSize: '0.75rem',
    display: 'block',
    marginTop: theme.spacing(0.5),
  },
  improvementContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  improvementText: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    fontSize: '0.875rem',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  pagination: {
    marginTop: theme.spacing(2),
  },
  card: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  cardContent: {
    backgroundColor: 'transparent',
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    }
  },
  showMoreButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(0, 135, 182, 0.08)', // Light blue hover state
    },
  },
});



// Format date to MM/DD/YYYY
const formatDate = (dateString) => {
  try {
    if (!dateString) return 'N/A';
    
    // Handle different date formats
    let date;
    if (dateString.includes('-') && dateString.includes(':')) {
      // Handle "2025-05-15-04:00" format
      const formattedDateString = dateString.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2})/, '$1T$2');
      date = new Date(formattedDateString);
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

// Calculate score improvement
const calculateImprovement = (firstScore, secondScore) => {
  return Math.round(secondScore - firstScore);
};

// Determine color based on score
const getScoreColor = (score) => {
  if (score >= 90) return '#4CAF50'; // Green for high scores
  if (score >= 70) return '#FFC107'; // Yellow/amber for medium scores
  return '#FF5722'; // Orange/red for low scores
};

// Get background color based on score
const getScoreBackgroundColor = (score) => {
  if (score >= 90) return 'rgba(76, 175, 80, 0.1)';
  if (score >= 70) return 'rgba(255, 193, 7, 0.1)';
  return 'rgba(255, 87, 34, 0.1)';
};

class ReviewTable extends Component {
  // Add constant for items to show
  static ITEMS_PER_LOAD = 5;

  state = {
    displayCount: ReviewTable.ITEMS_PER_LOAD, // Initially show 5 items
  };

  handleShowMore = () => {
    this.setState(prevState => ({
      displayCount: prevState.displayCount + ReviewTable.ITEMS_PER_LOAD
    }));
  };

  handleItemClick = (item) => {
    if (this.props.reviewFunc) {
      this.props.reviewFunc(item, true);
    }
  };

  render() {
    const { classes, data = [], loader = false, title } = this.props;
    const { displayCount } = this.state;


    const displayedData = data.slice(0, displayCount);
    const hasMoreItems = displayCount < data.length;

    const ScoreDisplay = ({ score, label, date, review }) => (
      <Box 
        className={classes.scoreContainer}
        style={{ 
          backgroundColor: getScoreBackgroundColor(score)
        }}
      >
        <Typography 
          className={classes.scoreNumber}
          style={{ color: getScoreColor(score) }}
        >
          {Math.round(score)}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {formatDate(date)}
        </Typography>
        <Typography 
          className={classes.scoreLabel}
          style={{ color: getScoreColor(score) }}
        >
          {label}
        </Typography>
        {review && (
          <Typography variant="caption" color="textSecondary" className={classes.wordsInfo}>
            {review.wordsCorrect || 0}/{review.wordCount || 0} words
          </Typography>
        )}
      </Box>
    );

    const ImprovementIndicator = ({ improvement }) => (
      <Box className={classes.improvementContainer}>
        <ArrowRightAlt color="primary" style={{ fontSize: 32 }} />
        <Typography className={classes.improvementText}>
          {improvement >= 0 ? `+${improvement}` : improvement}
        </Typography>
      </Box>
    );

    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.container}>
            <Typography variant="h5" component="h2" className={classes.header}>
              {title || <FormattedMessage id='BuddyBooks.ReviewTitle' />}
            </Typography>
            
            {loader ? (
              <Box className={classes.loadingContainer}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {displayedData.length === 0 ? (
                  <Typography variant="body1" color="textSecondary" align="center">
                    No review mode data available
                  </Typography>
                ) : (
                  <>
                    {displayedData.map((item, index) => {
                      // Handle both transformed and raw data structures
                      const firstScore = item['First Score'] || item.firstScore || 0;
                      const secondScore = item['Second Score'] || item.secondScore || 0;
                      const firstDate = item['First Attempt'] || item.firstDate || item.firstAttempt || '';
                      const secondDate = item['Second Attempt'] || item.secondDate || item.secondAttempt || '';
                      const text = item.text || '';
                      const firstReview = item.firstReview || {};
                      const secondReview = item.secondReview || {};
                      
                      const improvement = calculateImprovement(firstScore, secondScore);
                      
                      return (
                        <Paper 
                          key={index} 
                          elevation={3} 
                          className={classes.reviewItem}
                          onClick={() => this.handleItemClick(item)}
                        >
                          <Box className={classes.textContent}>
                            <Typography variant="body1">
                              {text}
                            </Typography>
                          </Box>
                          
                          <Divider style={{ margin: '16px 0' }} />
                          
                          <Grid container spacing={2} alignItems="center" justifyContent="center">
                            <Grid item xs={4} md={3} style={{ display: 'flex', justifyContent: 'center' }}>
                              <ScoreDisplay 
                                score={firstScore} 
                                label="First" 
                                date={firstDate}
                                review={firstReview}
                              />
                            </Grid>
                            
                            <Grid item xs={4} md={1} style={{ display: 'flex', justifyContent: 'center' }}>
                              <ImprovementIndicator improvement={improvement} />
                            </Grid>
                            
                            <Grid item xs={4} md={3} style={{ display: 'flex', justifyContent: 'center' }}>
                              <ScoreDisplay 
                                score={secondScore} 
                                label="Second" 
                                date={secondDate}
                                review={secondReview}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      );
                    })}
                    
                    {hasMoreItems && (
                      <Box display="flex" justifyContent="center" mt={2}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={this.handleShowMore}
                          className={classes.showMoreButton}
                        >
                          <FormattedMessage id='BuddyBooks.showMore' />
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }
}

export default withStyles(styles)(ReviewTable);


